import { useEffect, useRef, useState } from "preact/hooks";
import type { MessageRow, SearchParams } from "./api";
import { SELECT_ALL_MAX } from "./bulk";
import { listDate, sender } from "./format";
import { Icon } from "./icons";
import { NotKept, type Page, listMessages } from "./offline";
import { type Listed, type Overrides, shown, tick } from "./overrides";
import { SwipeRow, type SwipeSide } from "./SwipeRow";

const PAGE = 50;
const PULL_TRIGGER = 70;
/** Movement before a drag from the top counts as a pull or as a sideways swipe of a row. */
const SLOP = 8;

interface Props {
  params: SearchParams;
  /** Bumped by the app when the listing must be loaded again (the folder's change marker moved). */
  version: number;
  /** Changes the app made to messages (read, starred, moved, trashed) and to whole folders (marked read), shown at
   * once. */
  overrides: Overrides;
  folderReads: Overrides;
  /** The changes of the actions waiting on the device (outbox.ts), shown over the server's rows until they are done. */
  waiting: Overrides;
  selected: string | null;
  /** The messages ticked while several are being chosen (null: not choosing, a tap opens a message). */
  checked: Set<string> | null;
  emptyText: string;
  /** A search or the starred mail: folder tags on the rows, and how many there are (`noun`: results or messages). */
  showFolders: boolean;
  noun?: "result" | "message";
  onOpen: (id: string) => void;
  /** A row tapped while choosing: ticked, or no longer. */
  onCheck: (id: string) => void;
  /** The rows shown, each time they change while choosing (what select all ticks and the actions are done on), with
   * how many the listing has in all (null: not known yet) and whether every one of them is loaded. */
  onListed: (rows: MessageRow[], listing: { total: number | null; complete: boolean }) => void;
  /** Select all: load the rest of the listing, page after page, until it is complete or SELECT_ALL_MAX rows are loaded. */
  loadAll: boolean;
  /** A row swiped right: its star set, or removed. */
  onStar: (row: MessageRow) => void;
  /** A row swiped left: to Trash (deleted for good from Trash), after the app's Undo has had its time. */
  onTrash: (row: MessageRow) => void;
  onRefresh: () => Promise<void>;
  onError: (error: unknown) => void;
}

/** The rows of a page, with when it was asked for (the app's changes show over rows listed before they were done) and
 * whether the offline copy gave them (it has the actions waiting in the outbox already). */
function listed(page: Page, asked: number): Listed[] {
  return page.messages.map((m) => ({ ...m, listedAt: asked, copy: Boolean(page.offline) }));
}

/** A folder's messages or a search's results, page by page as the list is scrolled: from the server, or from the
 * offline copy when the server cannot be reached (offline.listMessages). A row swiped right stars it, left deletes it
 * (SwipeRow); one row at a time stays open, and a tap anywhere else in the list closes it. While several are being
 * chosen (`checked`) the rows have a tick box, a tap ticks one and they do not swipe. */
export function MessageList({ params, version, overrides, folderReads, waiting, selected, checked, emptyText, showFolders, noun = "result", onOpen, onCheck, onListed, loadAll, onStar, onTrash, onRefresh, onError }: Props) {
  const [rows, setRows] = useState<Listed[]>([]);
  const [total, setTotal] = useState<{ n: number; capped: boolean; offline: boolean } | null>(null);
  /** Every row of the listing is loaded (the last page came); or a page could not be loaded (`stalled`), so no more
   * will come by themselves (Select all stops asking; the next scroll to the end asks again). */
  const [complete, setComplete] = useState(false);
  const [stalled, setStalled] = useState(false);
  /** Neither the server nor the copy could list it: why, instead of the empty text. */
  const [notKept, setNotKept] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  /** The row swiped open, and which side. */
  const [swiped, setSwiped] = useState<{ id: string; side: SwipeSide } | null>(null);
  /** The touch that closed the row left open: its tap opens nothing. */
  const closing = useRef(false);
  const scroller = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const key = JSON.stringify(params);
  const generation = useRef(0);
  // The listing on screen, kept outside the render closures: the loaders read and write it, so a callback made
  // for an earlier render (the intersection observer fires between a render and its effects) can never fetch
  // the next page of one listing with the rows of another, or while a page is already loading.
  const listing = useRef<{ key: string; params: SearchParams; rows: Listed[]; loading: boolean; done: boolean }>({
    key: "", params, rows: [], loading: false, done: false,
  });

  const show = (next: Listed[], page: Page, finished: boolean) => {
    listing.current.rows = next;
    listing.current.done = finished;
    setRows(next);
    setTotal({ n: page.total, capped: page.capped, offline: Boolean(page.offline) });
    setComplete(finished);
    setStalled(false);
    setNotKept(null);
  };
  const failed = (gen: number, e: unknown) => {
    if (gen !== generation.current) return;
    setStalled(true);
    if (e instanceof NotKept) {
      listing.current.done = true;
      setNotKept(e.message);
    } else onError(e);
  };
  const finish = (gen: number) => {
    if (gen !== generation.current) return;
    listing.current.loading = false;
    setLoading(false);
  };

  // First page on a new listing; on a reload (version) as many rows as were loaded (up to 100), keeping the rest.
  useEffect(() => {
    const gen = ++generation.current;
    const same = listing.current.key === key;
    const have = same ? listing.current.rows : [];
    listing.current = { key, params, rows: have, loading: true, done: false };
    if (!same) {
      setSwiped(null);
      setRows([]);
      setTotal(null);
      setComplete(false);
      setStalled(false);
      setNotKept(null);
      scroller.current?.scrollTo({ top: 0 });
    }
    setLoading(true);
    const asked = tick();
    listMessages(params, Math.min(100, Math.max(PAGE, have.length)), 0)
      .then((page) => {
        if (gen !== generation.current) return;
        const ids = new Set(page.messages.map((m) => m.id));
        const tail = have.length > page.messages.length ? have.slice(page.messages.length).filter((m) => !ids.has(m.id)) : [];
        const next = [...listed(page, asked), ...tail];
        show(next, page, next.length >= page.total || page.messages.length === 0);
      })
      .catch((e) => failed(gen, e))
      .finally(() => finish(gen));
  }, [key, version]);

  /** The next page of the listing on screen (whatever render the caller came from). */
  const loadMore = () => {
    const current = listing.current;
    if (current.loading || current.done) return;
    const gen = generation.current;
    current.loading = true;
    setLoading(true);
    const asked = tick();
    listMessages(current.params, PAGE, current.rows.length)
      .then((page) => {
        if (gen !== generation.current) return;
        const have = listing.current.rows;
        const ids = new Set(have.map((m) => m.id));
        const next = [...have, ...listed({ ...page, messages: page.messages.filter((m) => !ids.has(m.id)) }, asked)];
        show(next, page, page.messages.length < PAGE || next.length >= page.total);
      })
      .catch((e) => failed(gen, e))
      .finally(() => finish(gen));
  };

  // Observed again after every render, so the end of the list still in view after a page loaded asks for the next
  // one. An observer outlives its render by a frame and may fire then; loadMore reads the listing's ref, so that
  // is harmless (and was not: it once fetched the new folder's second page onto the old folder's rows).
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && loadMore(), {
      root: scroller.current,
      rootMargin: "600px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  });

  // Pull to refresh: a downward drag that starts at the top of the list (one that starts sideways swipes a row).
  const start = useRef<{ x: number; y: number; pulling: boolean } | null>(null);
  const onTouchStart = (e: TouchEvent) => {
    closing.current = false;
    if (swiped && (e.target as HTMLElement).closest("li")?.dataset.id !== swiped.id) {
      setSwiped(null);
      closing.current = true;
    }
    const t = e.touches[0];
    start.current = scroller.current && scroller.current.scrollTop <= 0 && !refreshing ? { x: t.clientX, y: t.clientY, pulling: false } : null;
  };
  const onTouchMove = (e: TouchEvent) => {
    const s = start.current;
    if (!s) return;
    const dx = e.touches[0].clientX - s.x;
    const dy = e.touches[0].clientY - s.y;
    if (!s.pulling) {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        start.current = null;
        return;
      }
      s.pulling = true;
    }
    setPull(dy > 0 ? Math.min(dy * 0.5, 100) : 0);
  };
  const onTouchEnd = () => {
    if (start.current === null) return;
    start.current = null;
    if (pull >= PULL_TRIGGER) {
      setRefreshing(true);
      onRefresh().finally(() => setRefreshing(false));
    }
    setPull(0);
  };

  // the server gives a row the flags of the folder searched in (a listing is one), else of all its folders together; a
  // listing of the starred mail leaves out what lost its star here since
  const visible = rows.flatMap((row) => {
    const r = shown(row, overrides, folderReads, params.folder || null, waiting);
    return r && (params.flagged === undefined || r.flagged === params.flagged) ? [r] : [];
  });

  // Choosing: no row is left swiped open, and the app has the rows as shown (with what it changed since), and how many
  // there are in all. Select all asks for the rest of the listing, a page at a time, until it is complete or enough.
  useEffect(() => {
    if (checked) setSwiped(null);
  }, [checked !== null]);
  const listedKey = checked && `${total?.n ?? ""}:${+complete}${+stalled}:${visible.map((m) => `${m.id}:${+m.unread}${+m.flagged}:${m.folders.join("|")}`).join(",")}`;
  useEffect(() => {
    if (checked) onListed(visible, { total: total?.n ?? null, complete: complete || stalled });
  }, [listedKey]);
  useEffect(() => {
    if (loadAll && checked && !loading && !complete && !stalled && rows.length < SELECT_ALL_MAX) loadMore();
  }, [loadAll, loading, complete, stalled, rows.length]);

  return (
    <div
      class={`list ${checked ? "choosing" : ""}`}
      ref={scroller}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClickCapture={(e) => {
        if (!closing.current) return;
        closing.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div class={`pull ${refreshing ? "spinning" : ""}`} style={{ height: refreshing ? 44 : pull }}>
        <span class={pull >= PULL_TRIGGER || refreshing ? "ready" : ""}>
          <Icon name="refresh" />
        </span>
      </div>
      {total && showFolders && (
        <div class="list-total">
          {total.n === 0 ? `No ${noun}s` : `${total.n.toLocaleString()}${total.capped ? "+" : ""} ${total.n === 1 ? noun : `${noun}s`}`}
          {total.offline && ` in the mail kept on this device${noun === "result" ? ", newest first (words match where a word begins)" : ""}`}
        </div>
      )}
      <ul role="list">
        {visible.map((m) => (
          <li key={m.id} data-id={m.id}>
            <SwipeRow
              enabled={!checked && m.folders.length > 0}
              open={swiped?.id === m.id ? swiped.side : null}
              flagged={m.flagged}
              onOpen={(side) => setSwiped(side ? { id: m.id, side } : null)}
              onStar={() => onStar(m)}
              onDelete={() => onTrash(m)}
            >
              <button
                class={`row ${m.unread ? "unread" : ""} ${selected === m.id ? "selected" : ""} ${m.deleted_at ? "deleted" : ""} ${checked ? "choosing" : ""}`}
                role={checked ? "checkbox" : undefined}
                aria-checked={checked ? checked.has(m.id) : undefined}
                aria-disabled={checked && m.folders.length === 0 ? true : undefined}
                onClick={() => (checked ? m.folders.length > 0 && onCheck(m.id) : onOpen(m.id))}
              >
                {checked && (
                  // mail no longer in Yahoo has nothing to act on
                  <span class={`tick ${checked.has(m.id) ? "on" : ""} ${m.folders.length === 0 ? "unavailable" : ""}`} aria-hidden="true">
                    {checked.has(m.id) && <Icon name="check" size={16} />}
                  </span>
                )}
                <span class="row-top">
                  <span class="row-from">{sender(m.from_name, m.from_addr)}</span>
                  <span class="row-date">{listDate(m.date)}</span>
                </span>
                <span class="row-subject">
                  {m.flagged && (
                    <span class="flag">
                      <Icon name="star" size={14} title="Starred" filled />
                    </span>
                  )}
                  <span class="row-subject-text">{m.subject || "(no subject)"}</span>
                  {m.has_attachments && (
                    <span class="clip">
                      <Icon name="clip" size={14} title="Attachments" />
                    </span>
                  )}
                </span>
                <span class="row-snippet">{m.snippet}</span>
                {(showFolders || m.deleted_at || m.matched_attachment) && (
                  <span class="row-meta">
                    {m.deleted_at ? <span class="tag warn">Deleted</span> : showFolders && m.folders.map((f) => <span class="tag" key={f}>{f}</span>)}
                    {m.matched_attachment && <span class="tag">In: {m.matched_attachment}</span>}
                    {params.sent_to && m.sent_to && <span class="tag">Sent to {m.sent_to}</span>}
                  </span>
                )}
              </button>
            </SwipeRow>
          </li>
        ))}
      </ul>
      {!loading && visible.length === 0 && <p class="empty">{notKept ?? emptyText}</p>}
      <div ref={sentinel} class="sentinel">
        {loading && <span class="spinner" aria-label="Loading" />}
      </div>
    </div>
  );
}
